import React, { useLayoutEffect, useRef } from 'react';
import OrderDesign from '../OrderDesign';
import {
  MOBILE_HAND_CARD_BASE_HEIGHT,
  MOBILE_HAND_CARD_BASE_WIDTH,
} from '../components/mobile/mobileHandCarouselLayout';
import { getGeneralZoneRect } from '../utils/generalPortraitAnchor';
import './HandOrderGrantFlyOverlay.css';

const START_FRAC = 0.12;
const FLIGHT_MS = 680;

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

function getBattlefieldSlotBoundsRect(zone, index) {
  if (typeof document === 'undefined' || !zone || index == null) {
    return null;
  }
  const slotElement = document.querySelector(
    `[data-slot-zone="${zone}"][data-slot-index="${index}"]`
  );
  if (!slotElement) {
    return null;
  }
  const rect = slotElement.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  return rect;
}

function getBattlefieldSlotCenterPixels(zone, index) {
  const rect = getBattlefieldSlotBoundsRect(zone, index);
  if (!rect) {
    return null;
  }
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

function getGeneralPortraitCenterPixels(generalZone) {
  const rect = getGeneralZoneRect(generalZone);
  if (!rect) {
    return null;
  }
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

function resolveFlyOriginCenterPixels(grant) {
  if (grant?.originType === 'general' && grant?.generalZone) {
    return getGeneralPortraitCenterPixels(grant.generalZone);
  }
  if (grant?.zone != null && grant?.index != null) {
    return getBattlefieldSlotCenterPixels(grant.zone, grant.index);
  }
  return null;
}

function getHandCarouselLaneCenterRect() {
  if (typeof document === 'undefined') {
    return null;
  }
  const selectors = ['.order-carousel-container', '.order-carousel-mobile-wrapper'];
  for (const selector of selectors) {
    const el = document.querySelector(selector);
    if (!el) {
      continue;
    }
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') {
      continue;
    }
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return rect;
    }
  }
  return null;
}

function getHandCardBoundsRect(instanceId) {
  if (typeof document === 'undefined' || instanceId == null) {
    return null;
  }
  const id = String(instanceId);
  const selectors = [
    `.carousel-order-card[data-instance-id="${id}"]`,
    `.carousel-order-card-mobile[data-instance-id="${id}"]`,
  ];
  let cardEl = null;
  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) {
      cardEl = el;
      break;
    }
  }
  if (!cardEl) {
    return null;
  }
  const bounds = cardEl.querySelector('.order-design-card-bounds');
  const rect = bounds?.getBoundingClientRect?.();
  if (!rect || rect.width <= 0 || rect.height <= 0) {
    return null;
  }
  return rect;
}

/**
 * When the overlay anchor sits on the hand bounds center, the card artwork can
 * still be offset because of carousel shell transforms. Measure that constant
 * offset once so the flight path ends exactly on the hidden hand slot.
 */
function calibrateAnchorOffset(rootEl, targetRect) {
  const flyBounds = rootEl.querySelector('.order-design-card-bounds');
  if (!flyBounds) {
    return null;
  }

  const targetCenterX = targetRect.left + targetRect.width / 2;
  const targetCenterY = targetRect.top + targetRect.height / 2;

  rootEl.style.position = 'fixed';
  rootEl.style.left = `${targetCenterX}px`;
  rootEl.style.top = `${targetCenterY}px`;
  rootEl.style.transform = 'translate(-50%, -50%) scale(1)';
  rootEl.style.transformOrigin = 'center center';

  const flyRect = flyBounds.getBoundingClientRect();
  if (flyRect.width <= 0 || flyRect.height <= 0) {
    return null;
  }

  const flyCenterX = flyRect.left + flyRect.width / 2;
  const flyCenterY = flyRect.top + flyRect.height / 2;

  return {
    relX: flyCenterX - targetCenterX,
    relY: flyCenterY - targetCenterY,
  };
}

/**
 * Flies a known granted order from the provider battlefield slot into the matching hand slot.
 * Mirrors the hand carousel DOM shell so OrderDesign layout matches the hidden hand card.
 */
export const HandOrderGrantFlyOverlay = ({
  grant,
  order,
  cardScale = 1,
  mobileOuterScale = 1,
  isMobileHand = false,
  gameState = null,
  userId = null,
  onComplete,
}) => {
  const overlayRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const flightCompletedRef = useRef(false);

  useLayoutEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const grantKey = grant?.key ?? '';
  const grantZone = grant?.zone ?? '';
  const grantIndex = grant?.index;
  const grantGeneralZone = grant?.generalZone ?? '';
  const grantOriginType = grant?.originType ?? '';
  const grantDestinationType = grant?.destinationType ?? '';
  const grantDestinationZone = grant?.destinationZone ?? '';
  const grantDestinationIndex = grant?.destinationIndex;
  const grantOrderInstanceId =
    grant?.orderInstanceId != null ? String(grant.orderInstanceId) : '';

  useLayoutEffect(() => {
    flightCompletedRef.current = false;

    const isGeneralOrigin = grantOriginType === 'general' && grantGeneralZone;
    if (!grantOrderInstanceId || !order) {
      return undefined;
    }
    if (!isGeneralOrigin && (grantZone == null || grantIndex == null)) {
      return undefined;
    }

    const root = overlayRef.current;
    if (!root) {
      return undefined;
    }

    let cancelled = false;
    let rafId = 0;
    let startTime = null;
    let cachedStart = null;
    let anchorOffset = null;
    let measureAttempts = 0;
    let warmupFrames = 0;
    const usesDeploySlot = grantDestinationType === 'deploy_slot';
    const usesHandCarouselCenter = grantDestinationType === 'hand_carousel_center';

    const completeOnce = () => {
      if (flightCompletedRef.current || cancelled) {
        return;
      }
      flightCompletedRef.current = true;
      if (onCompleteRef.current) {
        onCompleteRef.current();
      }
    };

    root.style.opacity = '0';
    root.style.pointerEvents = 'none';

    const tick = (now) => {
      if (cancelled) {
        return;
      }

      if (warmupFrames < 2) {
        warmupFrames += 1;
        rafId = requestAnimationFrame(tick);
        return;
      }

      if (!cachedStart) {
        cachedStart = resolveFlyOriginCenterPixels(grant);
      }
      let targetRect = null;
      if (usesDeploySlot) {
        if (grantDestinationZone == null || grantDestinationIndex == null) {
          throw new Error('HandOrderGrantFlyOverlay: deploy_slot destination requires destinationZone and destinationIndex');
        }
        targetRect = getBattlefieldSlotBoundsRect(grantDestinationZone, grantDestinationIndex);
      } else if (usesHandCarouselCenter) {
        targetRect = getHandCarouselLaneCenterRect();
      } else {
        targetRect = getHandCardBoundsRect(grantOrderInstanceId);
      }

      if (!cachedStart || !targetRect) {
        measureAttempts += 1;
        if (measureAttempts < 28) {
          rafId = requestAnimationFrame(tick);
          return;
        }
        completeOnce();
        return;
      }

      if (!anchorOffset) {
        if (usesHandCarouselCenter || usesDeploySlot) {
          anchorOffset = { relX: 0, relY: 0 };
        } else {
          anchorOffset = calibrateAnchorOffset(root, targetRect);
        }
        if (!anchorOffset) {
          measureAttempts += 1;
          if (measureAttempts < 28) {
            rafId = requestAnimationFrame(tick);
            return;
          }
          completeOnce();
          return;
        }
      }

      if (startTime === null) {
        startTime = now;
      }

      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / FLIGHT_MS);
      const e = easeOutCubic(t);

      const targetCenterX = targetRect.left + targetRect.width / 2;
      const targetCenterY = targetRect.top + targetRect.height / 2;
      const endAnchorX = targetCenterX - anchorOffset.relX;
      const endAnchorY = targetCenterY - anchorOffset.relY;
      const x = cachedStart.x + (endAnchorX - cachedStart.x) * e;
      const y = cachedStart.y + (endAnchorY - cachedStart.y) * e;
      const scale = START_FRAC + (1 - START_FRAC) * e;

      root.style.position = 'fixed';
      root.style.left = `${x}px`;
      root.style.top = `${y}px`;
      root.style.transform = `translate(-50%, -50%) scale(${scale})`;
      root.style.transformOrigin = 'center center';
      root.style.opacity = t < 0.05 ? String(t / 0.05) : '1';
      root.style.zIndex = '100002';

      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        completeOnce();
      }
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [grantKey, grantZone, grantIndex, grantGeneralZone, grantOriginType, grantDestinationType, grantDestinationZone, grantDestinationIndex, grantOrderInstanceId, isMobileHand]);

  if (!grant || !order) {
    return null;
  }

  const orderDesign = (
    <OrderDesign
      order={order}
      count={0}
      minimal
      scale={cardScale}
      isOOR={!!order.isOOR}
      isPlayable={order.isPlayable}
      gameState={gameState}
      userId={userId}
      imageDraggable={false}
      lazyLoad
    />
  );

  return (
    <div ref={overlayRef} className="hand-order-grant-fly-overlay" aria-hidden="true">
      {isMobileHand ? (
        <div
          className="hand-order-grant-fly-overlay__mobile-shell"
          style={{
            width: `${MOBILE_HAND_CARD_BASE_WIDTH}px`,
            height: `${MOBILE_HAND_CARD_BASE_HEIGHT}px`,
            transform: `scale(${mobileOuterScale})`,
            transformOrigin: 'bottom left',
          }}
        >
          <div className="hand-order-grant-fly-overlay__mobile-inner">
            {orderDesign}
          </div>
        </div>
      ) : (
        <div className="hand-order-grant-fly-overlay__desktop-shell">
          <div className="hand-order-grant-fly-overlay__desktop-inner">
            <div className="hand-order-grant-fly-overlay__desktop-order-wrap">
              {orderDesign}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
